(() => {
  const SESSION_PREFIX = 'toccay_welcome_reset_v1_';

  const readSession = (key) => {
    try {
      return sessionStorage.getItem(`${SESSION_PREFIX}${key}`);
    } catch (error) {
      return null;
    }
  };

  const writeSession = (key, value = 'true') => {
    try {
      sessionStorage.setItem(`${SESSION_PREFIX}${key}`, value);
    } catch (error) {
      // The popup still works when storage is unavailable.
    }
  };

  const removeSession = (key) => {
    try {
      sessionStorage.removeItem(`${SESSION_PREFIX}${key}`);
    } catch (error) {
      // Ignore storage restrictions in private browsing contexts.
    }
  };

  const pushEventOnce = (name) => {
    const key = `event_${name}`;
    if (readSession(key)) return false;
    writeSession(key);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, popup_version: 'reset-v1' });
    return true;
  };

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const modal = root.querySelector('[data-welcome-reset-modal]');
    const dialog = root.querySelector('[role="dialog"]');
    const openButton = root.querySelector('[data-welcome-reset-open]');
    const closeButtons = root.querySelectorAll('[data-welcome-reset-close]');
    const form = root.closest('form');
    const email = root.querySelector('input[type="email"]');
    const error = root.querySelector('[data-welcome-reset-error]');
    const submitButton = root.querySelector('[data-welcome-reset-submit]');
    const shopButton = root.querySelector('[data-welcome-reset-shop]');
    const desktopDelay = Math.max(0, Number.parseInt(root.dataset.desktopDelay || '20000', 10));
    const mobileScroll = Math.min(100, Math.max(0, Number.parseInt(root.dataset.mobileScroll || '35', 10)));
    const isSuccess = root.dataset.success === 'true';
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const isPreviewReset = new URLSearchParams(window.location.search).get('toccay_offer_preview') === '1';

    let trigger = null;
    let timerId = 0;
    let retryTimerId = 0;
    let submitting = false;

    if (!modal || !dialog) return;

    if (isPreviewReset && !isSuccess) {
      ['seen', 'subscribed', 'event_popup_view', 'event_popup_close', 'event_popup_email_submit', 'event_popup_success']
        .forEach(removeSession);
    }

    const hasCompletedInvitation = () => Boolean(readSession('seen') || readSession('subscribed'));
    const markSeen = () => writeSession('seen');
    const markSubscribed = () => {
      writeSession('seen');
      writeSession('subscribed');
    };

    const focusableElements = () => Array.from(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);

    const hasBlockingModal = () => Array.from(document.querySelectorAll('[aria-modal="true"]:not(.search-modal), dialog[open]'))
      .some((element) => {
        if (element === dialog || root.contains(element)) return false;
        const style = window.getComputedStyle(element);
        return !element.hidden && style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      });

    const show = ({ source = null, success = false } = {}) => {
      if (!modal.hidden) return false;
      if (!success && readSession('subscribed')) return false;
      if (!success && !source && readSession('seen')) return false;

      trigger = source || document.activeElement;
      if (!success) markSeen();
      modal.hidden = false;
      document.body.classList.add('toccay-welcome-reset-open');
      window.setTimeout(() => {
        const focusTarget = success ? (shopButton || dialog) : (email || dialog);
        focusTarget.focus();
      }, 0);
      if (!success) pushEventOnce('popup_view');
      return true;
    };

    const hide = () => {
      if (modal.hidden) return false;
      modal.hidden = true;
      document.body.classList.remove('toccay-welcome-reset-open');
      pushEventOnce('popup_close');
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
      return true;
    };

    openButton?.addEventListener('click', () => show({ source: openButton }));
    closeButtons.forEach((button) => button.addEventListener('click', hide));

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        hide();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    email?.addEventListener('input', () => {
      if (error) error.hidden = true;
    });

    form?.addEventListener('submit', (event) => {
      if (email && !email.validity.valid) {
        event.preventDefault();
        if (error) error.hidden = false;
        email.focus();
        return;
      }
      if (submitting) {
        event.preventDefault();
        return;
      }

      submitting = true;
      pushEventOnce('popup_email_submit');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = submitButton.dataset.submittingText || 'SUBMITTING...';
      }
    });

    shopButton?.addEventListener('click', markSubscribed);

    if (isSuccess) {
      markSubscribed();
      show({ success: true });
      pushEventOnce('popup_success');
      return;
    }

    if (hasCompletedInvitation()) return;

    const clearTrigger = () => {
      if (timerId) window.clearTimeout(timerId);
      if (retryTimerId) window.clearTimeout(retryTimerId);
      window.removeEventListener('scroll', onMobileScroll);
    };

    const triggerPopup = () => {
      if (hasBlockingModal()) {
        if (!retryTimerId) {
          retryTimerId = window.setTimeout(() => {
            retryTimerId = 0;
            triggerPopup();
          }, 1500);
        }
        return;
      }
      if (show()) clearTrigger();
    };

    function onMobileScroll() {
      const documentElement = document.documentElement;
      const scrollableDistance = documentElement.scrollHeight - window.innerHeight;
      if (scrollableDistance <= 0) return;
      const progress = ((window.scrollY || documentElement.scrollTop || 0) / scrollableDistance) * 100;
      if (progress >= mobileScroll) triggerPopup();
    }

    if (isMobile) {
      window.addEventListener('scroll', onMobileScroll, { passive: true });
      onMobileScroll();
    } else {
      timerId = window.setTimeout(triggerPopup, desktopDelay);
    }
  };

  document.querySelectorAll('[data-toccay-welcome-reset]').forEach(init);
})();
